/**
 * Social Proof Section Component
 *
 * Displays real-time social proof metrics for experiences:
 * - Current viewers ("X people viewing now")
 * - 30-day booking count ("Booked Y times in the last 30 days")
 * - Last booked timestamp ("Last booked Z hours ago")
 *
 * Integrates with:
 * - GET /api/v1/analytics/social-proof/:experienceId (fetch metrics)
 * - POST /api/v1/analytics/social-proof/:experienceId/track-viewer (track page view)
 *
 * Epic: MVP 1.20.2-S - Social Proof Metrics System
 * Story: SCRUM-142
 * Location: apps/web/src/components/experiences/sections/SocialProof.jsx
 */

import { useState, useEffect } from 'react';
import { Eye, Calendar, Clock } from 'lucide-react';
import apiRequest from '../../../services/api';

/**
 * Get or create a persistent visitor ID for tracking
 * Stored in sessionStorage to maintain identity across page views
 */
function getVisitorId() {
  const key = 'outdoorguides_visitor_id';
  let visitorId = sessionStorage.getItem(key);

  if (!visitorId) {
    // Generate a simple unique ID (timestamp + random)
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(key, visitorId);
  }

  return visitorId;
}

/**
 * Fetch social proof metrics for an experience
 */
async function fetchSocialProofMetrics(experienceId) {
  const result = await apiRequest(`/api/v1/analytics/social-proof/${experienceId}`);
  return result.data;
}

/**
 * Track viewer for an experience (fire-and-forget)
 */
async function trackViewer(experienceId, visitorId) {
  try {
    await apiRequest(`/api/v1/analytics/social-proof/${experienceId}/track-viewer`, {
      method: 'POST',
      body: JSON.stringify({ visitorId, ttlSeconds: 300 })
    });
  } catch (error) {
    // Silent failure - tracking is non-critical
    console.warn('Failed to track viewer:', error);
  }
}

/**
 * Social Proof Badge Component
 */
function SocialProofBadge({ icon: Icon, label: _label, value, color = 'blue' }) {
  if (!value) {return null;}

  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700'
  };

  return (
    <div className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${colorClasses[color]}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm">
        <strong>{value}</strong>
      </span>
    </div>
  );
}

/**
 * Main Social Proof Component
 */
export default function SocialProof({ experienceId }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!experienceId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    const visitorId = getVisitorId();

    // Fetch metrics and track viewer
    async function loadMetrics() {
      try {
        // Track this page view (fire-and-forget)
        trackViewer(experienceId, visitorId);

        // Fetch current metrics
        const data = await fetchSocialProofMetrics(experienceId);

        if (mounted) {
          setMetrics(data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading social proof metrics:', err);
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    loadMetrics();

    // Refresh metrics every 30 seconds to show updated viewer count
    const intervalId = setInterval(async () => {
      try {
        const data = await fetchSocialProofMetrics(experienceId);
        if (mounted) {
          setMetrics(data);
        }
      } catch (err) {
        console.error('Error refreshing social proof metrics:', err);
      }
    }, 30000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [experienceId]);

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  // Don't render if there was an error or no metrics
  if (error || !metrics) {
    return null;
  }

  // Don't render if all metrics are zero/null (no social proof to show)
  // Note: Only show viewer count if 3+ people viewing (1-2 viewers isn't compelling social proof)
  const hasMetrics = metrics.currentViewers >= 3 ||
                     metrics.bookingCount30d > 0 ||
                     metrics.lastBookedHoursAgo !== null;

  if (!hasMetrics) {
    return null;
  }

  // Format last booked message
  const lastBookedMessage = metrics.lastBookedHoursAgo !== null
    ? metrics.lastBookedHoursAgo === 0
      ? 'Booked within the last hour'
      : metrics.lastBookedHoursAgo === 1
      ? 'Last booked 1 hour ago'
      : metrics.lastBookedHoursAgo < 24
      ? `Last booked ${metrics.lastBookedHoursAgo} hours ago`
      : metrics.lastBookedHoursAgo < 48
      ? 'Last booked yesterday'
      : `Last booked ${Math.floor(metrics.lastBookedHoursAgo / 24)} days ago`
    : null;

  return (
    <div className="mb-6 flex flex-wrap gap-3">
      {/* Current Viewers Badge - Only show if 3+ people viewing */}
      {metrics.currentViewers >= 3 && (
        <SocialProofBadge
          icon={Eye}
          label="Viewing Now"
          value={`${metrics.currentViewers} ${metrics.currentViewers === 1 ? 'person' : 'people'} viewing now`}
          color="blue"
        />
      )}

      {/* 30-Day Booking Count Badge */}
      {metrics.bookingCount30d > 0 && (
        <SocialProofBadge
          icon={Calendar}
          label="Last 30 Days"
          value={`Booked ${metrics.bookingCount30d} ${metrics.bookingCount30d === 1 ? 'time' : 'times'} in last 30 days`}
          color="green"
        />
      )}

      {/* Last Booked Badge */}
      {lastBookedMessage && (
        <SocialProofBadge
          icon={Clock}
          label="Recent Activity"
          value={lastBookedMessage}
          color="purple"
        />
      )}
    </div>
  );
}
